import { useCallback, useEffect, useRef, useState } from "react";

import { useRouter } from "next/router";

import Content from "components/content/Content";
import SchemaFormModal from "components/modal/schemaFormModal/SchemaFormModal";
import { useSubtotal } from "hooks/useSubtotal";
import { FormDocument } from "prismicio-types";
import {
  useGetProduct,
  useGetSchema,
  useGetToc,
} from "queries/product-queries";
import { pageview } from "tracking/gtm";
import { UseGetProductResponse } from "types/queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { getUpsellPriceFromProduct } from "utils/extras-utils";

import theme from "styles/theme.module.scss";

import { getPageUpsellPrice } from "../Preview";
import LayoutSelector from "../layoutSelector/LayoutSelector";
import Page from "../page/Page";

import styles from "./Pages.module.scss";

export type PageLayout = "one-page" | "two-page";

// If window.width > md, default to two-page layout
// Division to convert px to rems, parse to cut `rem` from the theme value
function isWindowWiderThanScreenMd() {
  return window.innerWidth / 16 > parseInt(theme.screenSm, 10);
}

export default function Pages() {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { data: schema } = useGetSchema();
  const { data: toc, status: tocStatus } = useGetToc();
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const { updateSubtotal } = useSubtotal(product?.museSlug);

  const [layout, setLayout] = useState<PageLayout>("one-page");
  const [activeSchemaForm, setActiveSchemaForm] = useState<{
    form: FormDocument;
    index: number;
  } | null>(null);

  const pagesRef = useRef<HTMLDivElement | null>(null);
  const lastPageHeightRef = useRef<number | null>(null);
  const lastScrollYRef = useRef<number | null>(null);

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  /**
   * Sort and filter in page edits to send active/progress to SchemaFormModal for display.
   *
   * This is complicated because:
   *
   * 1. The array of in-page edits is not in page order.
   * 2. Available in-page edits might change based on personalisations like gender
   *      - the TOC decides which pages are shown.
   * 3. In-page edits are referenced by page name, not index.
   */
  const activeInPageEdits = product.content.product.in_page_edits.filter(
    ({ muse_page_id }) =>
      (toc || []).findIndex(({ info }) => info?.pageName === muse_page_id) !==
      -1
  );
  const sortedActiveInPageEdits = [...activeInPageEdits].sort((a, b) =>
    (toc || []).findIndex(({ info }) => info?.pageName === a.muse_page_id) >
    (toc || []).findIndex(({ info }) => info?.pageName === b.muse_page_id)
      ? 1
      : -1
  );

  useEffect(() => {
    setLayout(isWindowWiderThanScreenMd() ? "two-page" : "one-page");
  }, []);

  useEffect(() => {
    if (
      !lastScrollYRef.current ||
      !lastPageHeightRef.current ||
      !pagesRef.current
    ) {
      return;
    }

    const scrollProgress = lastScrollYRef.current / lastPageHeightRef.current;
    const newPageHeight = pagesRef.current.scrollHeight - window.innerHeight;

    window.scrollTo({
      top: scrollProgress * newPageHeight,
    });

    lastPageHeightRef.current = newPageHeight;
  }, [layout]);

  function onEditButtonClicked({
    form,
    id,
  }: {
    form: FormDocument;
    id?: string;
  }) {
    const index = sortedActiveInPageEdits.findIndex(
      ({ muse_page_id }) => muse_page_id === id
    );
    setActiveSchemaForm({ form, index });

    // Manually set the pageview to avoid faffing with hashes in the URL
    pageview(router.asPath + "#" + form.data.tracking_id);
  }

  function onModalDismiss(data?: qs.ParsedQs) {
    if (data) {
      const priceDetail = getPageUpsellPrice(
        data,
        product as UseGetProductResponse
      );

      if (priceDetail) {
        updateSubtotal(priceDetail);
      }
    }

    setActiveSchemaForm(null);
    // We track when a form is opened, so track when it closes too
    pageview(router.asPath);
  }

  /**
   * When 'next' or 'previous' edit is clicked,
   * reload the modal with the appropriate form.
   */
  function onModalStepEdit(direction: "next" | "previous", data?: qs.ParsedQs) {
    if (!activeSchemaForm || !product || !toc) {
      if (data) {
        const priceDetail = getPageUpsellPrice(
          data,
          product as UseGetProductResponse
        );
        if (priceDetail) {
          updateSubtotal(priceDetail);
        }
      }
      return;
    }

    if (
      direction === "next" &&
      activeSchemaForm.index < sortedActiveInPageEdits.length - 1
    ) {
      return setActiveSchemaForm({
        index: activeSchemaForm.index + 1,
        form: (sortedActiveInPageEdits[activeSchemaForm.index + 1] as any).form,
      });
    }

    if (direction === "previous" && activeSchemaForm.index > 0) {
      return setActiveSchemaForm({
        index: activeSchemaForm.index - 1,
        form: (sortedActiveInPageEdits[activeSchemaForm.index - 1] as any).form,
      });
    }

    setActiveSchemaForm(null);
  }

  function onSetLayout(layout: PageLayout) {
    /**
     * We can't reliably tell when the page has reached its max height,
     * so initialise previous height on first layout click.
     */
    if (!lastPageHeightRef.current && pagesRef.current) {
      lastPageHeightRef.current =
        pagesRef.current.scrollHeight - window.innerHeight;
      // ^ Deduct window height as scroll position is relative to the top left corner
    }

    lastScrollYRef.current = window.scrollY;

    setLayout(layout);
  }

  useEffect(() => {
    const handleScroll = () => {
      if (hasUserScrolled) {
        window.removeEventListener("scroll", handleScroll);
        return;
      }

      setHasUserScrolled(true);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasUserScrolled]);

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!schema) {
    return null;
  }

  return (
    <>
      <div
        data-opt-testid="layoutSelector"
        className={[
          styles.pagesContainer,
          styles[layout],
          styles.layerSelectorSection,
        ].join(" ")}
      >
        <LayoutSelector layout={layout} setLayout={onSetLayout} />
      </div>

      <div
        ref={pagesRef}
        data-opt-testid="pageContainer"
        className={[
          styles.pagesContainer,
          styles[layout],
          styles.nudgeAnimation,
          // We add an extra class instead of removing it to transition
          // smoothly from infinite to single iteration and avoid any jitter.
          hasUserScrolled && styles.singleNundgeAnimation,
        ].join(" ")}
        style={{
          // Ensure page height never surpases 65% of the intial window height
          maxWidth:
            layout === "two-page"
              ? `min(
                calc(${window.innerHeight * 0.65}px * ${schema.aspect} * 2),
                ${parseInt(theme.screenXl, 10)}rem
              )`
              : `min(
                calc(${window.innerHeight * 0.65}px * ${schema.aspect}),
                ${parseInt(theme.screenMd, 10)}rem
              )`,
        }}
      >
        {product && (
          <h1 className={styles.title}>
            <Content field={product.content.product.title} />
          </h1>
        )}
        <ol className={styles.pages}>
          {tocStatus === "loading" && <SkeletonPages />}

          {tocStatus === "success" &&
            toc.map((page, index) => (
              <Page
                key={page.info?.pageName ?? index}
                layout={layout}
                page={page}
                // Start page numbers from 1
                pageNumber={index}
                onEditButtonClicked={(form) =>
                  onEditButtonClicked({ form, id: page.info?.pageName })
                }
              />
            ))}
        </ol>
      </div>

      {activeSchemaForm && (
        <SchemaFormModal
          formDocument={activeSchemaForm.form}
          isOpen={!!activeSchemaForm}
          onDismiss={(data) => onModalDismiss(data as qs.ParsedQs)}
          title="Edit page"
          // Only show Next/Previous Edit buttons when enabled on Prismic
          {...(product.content.product.enable_next_previous_edit_buttons
            ? {
                onStepEdit: onModalStepEdit,
                currentEditIndex: activeSchemaForm.index,
                numberOfEdits: sortedActiveInPageEdits.length,
              }
            : {})}
        />
      )}
    </>
  );
}

function SkeletonPages() {
  return (
    <>
      {Array(31)
        .fill("")
        .map((_, i) => (
          <li key={i} className={styles.skeletonPage} />
        ))}
    </>
  );
}
